import { NA, phaseLabel, signedUsd, usd } from '../../utilities/format';

/**
 * `/v2/account/public/<login>/?platform=` → the public performance page
 * (1:32487). One place for every derived string on it, so the page itself stays
 * markup.
 */

const PLATFORM = { mt5: 'MetaTrader 5', mt4: 'MetaTrader 4' };

const DATE = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

const TIME = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const fmt = (formatter, value) => {
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? formatter.format(parsed) : NA;
};

/** "AUDCAD.pro" — the broker's suffix is not part of the instrument. */
const baseSymbol = (symbol) => String(symbol ?? '').split('.')[0];

/**
 * Returns arrive as a percentage already, and a single trade's is a fraction
 * of one (-0.0029) — fixing two decimals would print every row as -0.00%.
 */
const pct = (value, { signed = true } = {}) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return NA;
  const places = n !== 0 && Math.abs(n) < 0.01 ? 4 : 2;
  return `${signed && n > 0 ? '+' : ''}${n.toFixed(places)}%`;
};

const num = (value, fallback = NA) =>
  Number.isFinite(Number(value)) ? Number(value).toLocaleString('en-US') : fallback;

/** "50000" → "50K", the way every other page writes an account size. */
const sizeLabel = (value) =>
  Number.isFinite(Number(value)) && Number(value) ? `${Math.round(Number(value) / 1000)}K` : null;

/**
 * The footer promises reference numbers are never part of a public page, so
 * the login is shown the way the design masks it, last three digits only.
 */
const maskedLogin = (loginId, t) => {
  const digits = String(loginId ?? '');
  return digits ? t('Account #••••{{digits}}', { digits: digits.slice(-3) }) : null;
};

/**
 * The page hands in the app's `t` to translate the strings built here. Taken
 * as an argument rather than imported, so this module (and its test) does not
 * load the locale bundles; left out, the strings come back in English.
 */
const english = (text, vars) =>
  vars ? text.replace(/\{\{(\w+)\}\}/g, (match, name) => (vars[name] ?? match)) : text;

const titleCase = (value) =>
  value ? String(value).charAt(0).toUpperCase() + String(value).slice(1) : null;

/** The whole page, as its sections read it. */
export const toShareView = (payload, t = english) => {
  const account = payload?.account ?? {};
  const equity = payload?.equity ?? {};
  const stats = payload?.statistics ?? {};
  const payouts = payload?.payouts ?? {};

  const change = Number(equity.change_since_start_percent);
  const changeKnown = Number.isFinite(change);

  return {
    // The endpoint reads live rather than storing a snapshot, so the page is
    // dated the moment it was opened.
    snapshotDate: DATE.format(Date.now()),

    accountName: [sizeLabel(account.account_size), t(phaseLabel(account.stage))].filter(Boolean).join(' ') || NA,
    accountMeta: [
      PLATFORM[account.platform] ?? account.platform,
      t(titleCase(account.status)),
      maskedLogin(account.login_id, t),
    ]
      .filter(Boolean)
      .join(' · '),

    headline: [
      {
        icon: 'stat-balance',
        label: 'Equity',
        value: usd(equity.equity, { decimals: 2 }).replace('$', ''),
        unit: '$',
        note: changeKnown ? `${change < 0 ? t('Down') : t('Up')} ` : '',
        accent: changeKnown ? pct(change) : null,
        accentTone: change < 0 ? 'down' : 'up',
        noteEnd: changeKnown ? ` ${t('since start')}` : t('No change since start'),
      },
      {
        icon: 'metrics-objectives',
        label: 'Win rate',
        value: num(equity.win_rate),
        unit: '%',
        note: t('{{wins}} wins, {{losses}} losses', { wins: num(equity.wins, 0), losses: num(equity.losses, 0) }),
      },
      {
        icon: 'chart-profit',
        label: 'Profit factor',
        value: num(equity.profit_factor),
        note: equity.risk_to_reward?.label
          ? t('Risk to reward {{ratio}}', { ratio: equity.risk_to_reward.label })
          : t('Risk to reward not available'),
      },
    ],

    // Passed through as they come: `CertificateArt` reads the same row the
    // certificates list hands it.
    certificates: payload?.certificates ?? [],

    statistics: {
      note: [stats.total_trades != null && t('{{n}} trades', { n: num(stats.total_trades) }), t(stats.phase)]
        .filter(Boolean)
        .join(' · '),
      stats: [
        { label: 'Total trades', value: num(stats.total_trades) },
        { label: 'Wins / losses', value: `${num(stats.wins, 0)} / ${num(stats.losses, 0)}` },
        {
          label: 'Net profit',
          value: signedUsd(stats.net_profit, { decimals: 2 }),
          tone: Number(stats.net_profit) < 0 ? 'down' : 'up',
        },
        { label: 'Max drawdown', value: pct(stats.max_drawdown, { signed: false }) },
        { label: 'Expectancy', value: usd(stats.expectancy, { decimals: 2 }) },
        { label: 'Average hold', value: stats.average_hold ?? NA },
      ],
      symbols: (payload?.volume_by_symbol ?? []).map((row) => ({
        symbol: baseSymbol(row.symbol),
        pct: Math.round(Number(row.percentage) || 0),
      })),
    },

    trades: {
      shown: t('{{shown}} of {{total}} most recent', {
        shown: (payload?.recent_trades ?? []).length,
        total: num(payload?.recent_trades_total, 0),
      }),
      net: signedUsd(payload?.recent_trades_sum, { decimals: 2 }),
      netTone: Number(payload?.recent_trades_sum) < 0 ? 'down' : 'up',
      rows: (payload?.recent_trades ?? []).map((row, i) => ({
        // Five trades can share a symbol and a timestamp, so the row's place in
        // the list is what tells them apart.
        id: i,
        symbol: baseSymbol(row.symbol),
        detail: [t(row.side), row.lots != null && t('{{lots}} lots', { lots: row.lots }), fmt(TIME, row.time)]
          .filter(Boolean)
          .join(' · '),
        pnl: signedUsd(row.profit, { decimals: 2 }),
        meta: [pct(row.return_pct), t(row.exit_reason)].filter(Boolean).join(' · '),
      })),
    },

    payouts: {
      pulse: [
        { label: 'Lifetime withdrawn', value: usd(payouts.lifetime_withdrawn) },
        { label: 'Cleared payouts', value: num(payouts.cleared_payouts, '0') },
        { label: 'Avg. approved size', value: usd(payouts.avg_approved_size) },
        { label: 'Latest payout', value: payouts.latest_payout ? fmt(DATE, payouts.latest_payout) : NA },
      ],
      // ponytail: `top_payouts` has only ever come back empty, so its row shape
      // is read off the payout history's names — confirm when one lands.
      top: (payouts.top_payouts ?? []).map((row, i) => ({
        id: row.id ?? row.reference ?? i,
        requested: fmt(DATE, row.requested_on ?? row.created_at ?? row.date),
        amount: usd(row.amount, { decimals: 2 }),
        status: row.status ?? NA,
      })),
    },
  };
};
